import React from 'react';
import './Pages/PagesCss/PrivacyPolicy.css';

const ContentPolicy = () => {
  return (
    <div className="privacy-policy">
      {/* Header Section */}
      <section className="policy-header">
        <div className="container">
          <div className="header-content">
            <h1>Content Policy</h1>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p className="header-description">
              Guidelines for content usage, user contributions, and the educational nature of our 
              financial calculator platform. This policy ensures quality and reliability for all users.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="policy-content">
        <div className="container">
          <div className="policy-grid">
            {/* Table of Contents */}
            <aside className="table-of-contents">
              <h3>Table of Contents</h3>
              <nav>
                <ul>
                  <li><a href="#purpose">Purpose & Mission</a></li>
                  <li><a href="#content-standards">Content Standards</a></li>
                  <li><a href="#prohibited-content">Prohibited Content</a></li>
                  <li><a href="#user-contributions">User Contributions</a></li>
                  <li><a href="#calculator-content">Calculator Content</a></li>
                  <li><a href="#educational-content">Educational Content</a></li>
                  <li><a href="#intellectual-property">Intellectual Property</a></li>
                  <li><a href="#content-moderation">Content Moderation</a></li>
                  <li><a href="#corrections">Corrections & Updates</a></li>
                  <li><a href="#accessibility">Accessibility</a></li>
                  <li><a href="#reporting">Reporting Violations</a></li>
                  <li><a href="#contact">Contact Information</a></li>
                </ul>
              </nav>
            </aside>

            {/* Content Policy Sections */}
            <div className="policy-sections">

               <p>
  This Content Policy applies to all websites and subdomains operated by D-Tech Studios for noobtobull, including <a href="https://noobtobull.com" target="_blank" rel="noopener noreferrer">https://noobtobull.com</a> and <a href="https://blog.noobtobull.com" target="_blank" rel="noopener noreferrer">https://blog.noobtobull.com</a>.
</p>
              <section id="purpose" className="policy-section">
                <h2>1. Purpose & Mission</h2>
                <div className="section-content">
                  <div className="security-features">
                    <h3>Our Mission</h3>
                    <p>
                      <strong>noobtobull is committed to providing accurate, educational financial 
                      calculator tools</strong> that help users understand financial concepts and 
                      perform calculations for learning and planning purposes.
                    </p>
                  </div>

                  <h3>1.1 Educational Focus</h3>
                  <p>
                    All content on our platform is designed with an educational purpose. We aim to:
                  </p>
                  <ul>
                    <li>Demystify financial concepts through interactive tools</li>
                    <li>Provide reliable calculation methods for educational purposes</li>
                    <li>Help users learn about financial planning and analysis</li>
                    <li>Maintain high standards of accuracy and clarity</li>
                  </ul>

                  <h3>1.2 Target Audience</h3>
                  <p>
                    Our content is designed for individuals seeking to learn about financial 
                    concepts, from beginners to advanced users. We strive to make complex 
                    financial topics accessible and understandable.
                  </p>
                </div>
              </section>

              <section id="content-standards" className="policy-section">
                <h2>2. Content Standards</h2>
                <div className="section-content">
                  <h3>2.1 Accuracy and Reliability</h3>
                  <p>
                    We maintain high standards for all content on our platform:
                  </p>
                  <ul>
                    <li><strong>Mathematical Accuracy:</strong> Calculations use standard financial formulas</li>
                    <li><strong>Clear Documentation:</strong> Methods and assumptions are clearly explained</li>
                    <li><strong>Regular Review:</strong> Content is periodically reviewed for accuracy</li>
                    <li><strong>Source Transparency:</strong> We cite sources for financial formulas and data</li>
                  </ul>

                  <h3>2.2 Educational Quality</h3>
                  <p>All content must meet our educational quality standards:</p>
                  <ul>
                    <li>Clear explanations of financial concepts</li>
                    <li>Appropriate context for calculations</li>
                    <li>Balanced presentation of different approaches</li>
                    <li>Accessible language for diverse audiences</li>
                  </ul>

                  <div className="security-features">
                    <h3>Quality Commitment</h3>
                    <p>
                      <strong>We are committed to maintaining the highest educational standards</strong> 
                      while clearly distinguishing between educational content and financial advice.
                    </p>
                  </div>
                </div>
              </section>

              <section id="prohibited-content" className="policy-section">
                <h2>3. Prohibited Content</h2>
                <div className="section-content">
                  <div className="security-features">
                    <h3>Zero Tolerance</h3>
                    <p>
                      <strong>We prohibit content that could mislead, harm, or provide false 
                      financial guidance</strong> to our users. This includes any content that 
                      could be construed as financial advice.
                    </p>
                  </div>

                  <h3>3.1 Specifically Prohibited</h3>
                  <p>The following types of content are strictly prohibited:</p>
                  <ul>
                    <li><strong>Financial Advice:</strong> Specific investment recommendations or advice</li>
                    <li><strong>Guarantees:</strong> Promises of specific financial outcomes or returns</li>
                    <li><strong>Misleading Claims:</strong> Content that could be misinterpreted as guarantees</li>
                    <li><strong>Unauthorized Professional Content:</strong> Content claiming professional authority without proper disclosure</li>
                  </ul>

                  <h3>3.2 General Prohibitions</h3>
                  <p>We also prohibit:</p>
                  <ul>
                    <li>Spam, promotional content, or advertisements</li>
                    <li>False or misleading information</li>
                    <li>Harassing, abusive, or offensive content</li>
                    <li>Copyright-infringing material</li>
                    <li>Content that violates applicable laws</li>
                  </ul>
                </div>
              </section>

              <section id="user-contributions" className="policy-section">
                <h2>4. User Contributions</h2>
                <div className="section-content">
                  <h3>4.1 Feedback and Suggestions</h3>
                  <p>
                    We welcome user feedback and suggestions for improving our calculators. 
                    However, all user contributions must adhere to our content standards and 
                    cannot include:
                  </p>
                  <ul>
                    <li>Specific investment recommendations</li>
                    <li>Personal financial advice</li>
                    <li>Promotional content</li>
                    <li>Unverified financial strategies</li>
                  </ul>

                  <h3>4.2 Content Licensing</h3>
                  <p>
                    By submitting content to us, you grant noobtobull a worldwide, non-exclusive, 
                    royalty-free license to use, modify, and incorporate your suggestions into 
                    our platform, with appropriate attribution when possible.
                  </p>

                  <div className="security-features">
                    <h3>User Content Responsibility</h3>
                    <p>
                      <strong>Users are responsible for ensuring their contributions</strong> 
                      comply with this policy and do not infringe on third-party rights.
                    </p>
                  </div>
                </div>
              </section>

              <section id="calculator-content" className="policy-section">
                <h2>5. Calculator Content Standards</h2>
                <div className="section-content">
                  <h3>5.1 Calculation Methodology</h3>
                  <p>All calculators on our platform must:</p>
                  <ul>
                    <li>Use standard, verifiable financial formulas</li>
                    <li>Clearly state assumptions and limitations</li>
                    <li>Provide context for interpreting results</li>
                    <li>Include appropriate disclaimers</li>
                  </ul>

                  <h3>5.2 Input and Output Standards</h3>
                  <p>We maintain consistent standards for calculator interfaces:</p>
                  <ul>
                    <li>Clear labeling of input fields</li>
                    <li>Appropriate units and formatting</li>
                    <li>Consistent result presentation</li>
                    <li>Educational explanations of outputs</li>
                  </ul>

                  <div className="rights-grid">
                    <div className="right-item">
                      <h4>Formula Transparency</h4>
                      <p>All calculation methods are documented and verifiable</p>
                    </div>
                    <div className="right-item">
                      <h4>Assumption Clarity</h4>
                      <p>All assumptions and limitations are clearly stated</p>
                    </div>
                    <div className="right-item">
                      <h4>Educational Focus</h4>
                      <p>Calculators prioritize learning over decision-making</p>
                    </div>
                    <div className="right-item">
                      <h4>Regular Updates</h4>
                      <p>Content is reviewed and updated regularly</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="educational-content" className="policy-section">
                <h2>6. Educational Content Guidelines</h2>
                <div className="section-content">
                  <h3>6.1 Learning Objectives</h3>
                  <p>
                    All educational content must have clear learning objectives and help users:
                  </p>
                  <ul>
                    <li>Understand financial concepts and principles</li>
                    <li>Learn calculation methods and applications</li>
                    <li>Develop financial literacy skills</li>
                    <li>Make informed decisions about seeking professional advice</li>
                  </ul>

                  <h3>6.2 Balanced Presentation</h3>
                  <p>
                    We present multiple perspectives and approaches where applicable, 
                    ensuring users understand that there are often different valid methods 
                    in financial analysis.
                  </p>

                  <div className="security-features">
                    <h3>Educational Integrity</h3>
                    <p>
                      <strong>We maintain strict boundaries between education and advice.</strong> 
                      Our content teaches concepts and methods without recommending specific 
                      financial actions or products.
                    </p>
                  </div>
                </div>
              </section>

              <section id="intellectual-property" className="policy-section">
                <h2>7. Intellectual Property Rights</h2>
                <div className="section-content">
                  <h3>7.1 Our Content</h3>
                  <p>
                    All calculator tools, educational content, website design, and proprietary 
                    methodologies are owned by noobtobull and protected by intellectual property laws.
                  </p>

                  <h3>7.2 User Rights</h3>
                  <p>
                    Users may use our calculators and educational content for personal, 
                    non-commercial educational purposes. Commercial use requires explicit 
                    written permission.
                  </p>

                  <h3>7.3 Third-Party Content</h3>
                  <p>
                    We respect third-party intellectual property rights and properly attribute 
                    external sources. Any use of third-party content is done with appropriate 
                    permissions or under fair use for educational purposes.
                  </p>

                  <div className="security-features">
                    <h3>Attribution</h3>
                    <p>
                      <strong>We attribute financial formulas and methodologies</strong> to their 
                      original sources whenever possible, maintaining academic integrity in our 
                      educational approach.
                    </p>
                  </div>
                </div>
              </section>

              <section id="content-moderation" className="policy-section">
                <h2>8. Content Moderation</h2>
                <div className="section-content">
                  <h3>8.1 Review Process</h3>
                  <p>
                    All content undergoes regular review to ensure compliance with this policy. 
                    Our moderation process includes:
                  </p>
                  <ul>
                    <li>Initial content review before publication</li>
                    <li>Regular accuracy audits</li>
                    <li>User feedback evaluation</li>
                    <li>Compliance checks with current regulations</li>
                  </ul>

                  <h3>8.2 Violation Handling</h3>
                  <p>
                    Content found to violate this policy will be:
                  </p>
                  <ul>
                    <li>Immediately removed or corrected</li>
                    <li>Subject to investigation</li>
                    <li>Documented for process improvement</li>
                  </ul>

                  <h3>8.3 Appeal Process</h3>
                  <p>
                    Users may appeal content removal decisions by contacting us with a 
                    detailed explanation of why they believe the content complies with this policy.
                  </p>
                </div>
              </section>

              <section id="corrections" className="policy-section">
                <h2>9. Corrections & Updates</h2>
                <div className="section-content">
                  <h3>9.1 Error Correction</h3>
                  <p>
                    We are committed to promptly correcting errors in our content. When errors 
                    are identified, we:
                  </p>
                  <ul>
                    <li>Immediately investigate reported issues</li>
                    <li>Correct verified errors promptly</li>
                    <li>Document corrections for transparency</li>
                    <li>Notify users of significant corrections when appropriate</li>
                  </ul>

                  <h3>9.2 Content Updates</h3>
                  <p>
                    We regularly update content to reflect:
                  </p>
                  <ul>
                    <li>Changes in financial regulations</li>
                    <li>New industry standards and practices</li>
                    <li>User feedback and suggestions</li>
                    <li>Advances in financial education methodologies</li>
                  </ul>

                  <div className="security-features">
                    <h3>Continuous Improvement</h3>
                    <p>
                      <strong>We are committed to continuous improvement</strong> of our content 
                      and welcome user feedback to help maintain the highest educational standards.
                    </p>
                  </div>
                </div>
              </section>

              <section id="accessibility" className="policy-section">
                <h2>10. Accessibility Standards</h2>
                <div className="section-content">
                  <h3>10.1 Inclusive Design</h3>
                  <p>
                    We strive to make our content accessible to all users, including those 
                    with disabilities. Our accessibility efforts include:
                  </p>
                  <ul>
                    <li>Screen reader compatibility</li>
                    <li>Keyboard navigation support</li>
                    <li>Color contrast considerations</li>
                    <li>Clear and simple language</li>
                  </ul>

                  <h3>10.2 Continuous Accessibility Improvement</h3>
                  <p>
                    We regularly review and improve accessibility features and welcome 
                    feedback from users about accessibility needs.
                  </p>

                  <div className="rights-grid">
                    <div className="right-item">
                      <h4>Visual Accessibility</h4>
                      <p>High contrast ratios and clear typography</p>
                    </div>
                    <div className="right-item">
                      <h4>Navigation</h4>
                      <p>Keyboard and screen reader friendly</p>
                    </div>
                    <div className="right-item">
                      <h4>Content Structure</h4>
                      <p>Clear headings and logical content flow</p>
                    </div>
                    <div className="right-item">
                      <h4>Language</h4>
                      <p>Clear, simple explanations for complex topics</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="reporting" className="policy-section">
                <h2>11. Reporting Policy Violations</h2>
                <div className="section-content">
                  <h3>11.1 How to Report</h3>
                  <p>
                    Users can report potential policy violations by contacting us with:
                  </p>
                  <ul>
                    <li>Specific description of the concern</li>
                    <li>Location of the content in question</li>
                    <li>Reasons for believing it violates this policy</li>
                    <li>Any supporting evidence or context</li>
                  </ul>

                  <h3>11.2 Investigation Process</h3>
                  <p>
                    All reports are promptly investigated by our content review team. 
                    We typically respond to reports within 3-5 business days.
                  </p>

                  <div className="security-features">
                    <h3>Confidential Reporting</h3>
                    <p>
                      <strong>We treat all reports confidentially</strong> and protect reporters 
                      from retaliation. Good-faith reports help us maintain content quality.
                    </p>
                  </div>
                </div>
              </section>

              <section id="contact" className="policy-section">
                <h2>12. Contact Information</h2>
                <div className="section-content">
                  <p>
                    For questions about this Content Policy or to report potential violations, 
                    please contact us:
                  </p>
                  <div className="contact-info">
                    <div className="contact-item">
                      <strong>General Questions:</strong> support@noobtobull.com
                    </div>
                    <div className="contact-item">
                      <strong>Address:</strong> 2568 Madison Ave, League City, TX 77573
                    </div>
                  </div>

                  <div className="security-features">
                    <h3>Policy Updates</h3>
                    <p>
                      <strong>This Content Policy may be updated periodically</strong> to reflect 
                      changes in our services, industry standards, or regulatory requirements. 
                      Continued use of our platform constitutes acceptance of updated policies.
                    </p>
                  </div>

                  <p>
      For more information on data collection, privacy, and user responsibilities, see our <a href="/PrivacyPolicy">Privacy Policy</a> and <a href="/Disclaimer">Disclaimer</a>.
    </p>
                </div>
              </section>

              <section id="third-party-services" className="policy-section">
  <h2>13. Third-Party Services</h2>
  <div className="section-content">
    <p>
      We may use third-party services such as Google Analytics or advertising partners 
      to improve our services. These services may collect anonymized data about user 
      interactions with our platform, including content usage.
    </p>
  </div>
</section>

            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="policy-actions">
        <div className="container">
          <div className="actions-content">
            <h3>Maintaining Quality Standards</h3>
            <p>Help us maintain the highest educational standards by following these guidelines and reporting any concerns.</p>
            <div className="action-buttons">
              <a href="/Contact" className="btn btn-primary">Report Content</a>
              <a href="/BrokerageCalculator" className="btn btn-secondary">Use Calculators</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContentPolicy;